import { useState, useEffect, useCallback } from 'react';
import {
  fetchProjectSourceApplicationAssets,
  fetchProjectSupplementMaterials,
} from '@/service/api/project';
import { formatDate } from './utils';
import AddSupplementMaterialsModal from './AddSupplementMaterialsModal';
import PortfolioPreviewModal from '@/components/common/PortfolioPreviewModal';
import Toast from '@/components/common/Toast';

interface MaterialsTabProps {
  project: Api.Project.ProjectDetailResponse;
}

/** 格式化资料分类标签 */
const formatAttachmentCategory = (category?: string, isSupplement?: boolean) => {
  if (!category) {
    return isSupplement ? '补充资料' : '常规资料';
  }
  const categoryMap: Record<string, string> = {
    APPLICATION_COVER: '封面',
    APPLICATION_TRAILER: '宣传片',
    APPLICATION_POSTER: '海报',
    COPYRIGHT_CHAIN: '版权授权链',
    COPYRIGHT_CORE: '核心版权证明',
    COPYRIGHT_AUXILIARY: '辅助版权资料',
    QUALIFICATION: '资质能力证明',
    EPISODE: '剧集素材',
    OTHER: '其他补充资料',
  };
  return categoryMap[category] || category || (isSupplement ? '补充资料' : '常规资料');
};

/** 判断文件是否为图片 */
const isImageFile = (fileName?: string, fileType?: string) => {
  const str = ((fileName || '') + ' ' + (fileType || '')).toLowerCase();
  return /\.(png|jpg|jpeg|gif|webp|svg|bmp|ico)($|\?)/i.test(str) || str.includes('image');
};

/** 判断文件是否为视频 */
const isVideoFile = (fileName?: string, fileType?: string) => {
  const str = ((fileName || '') + ' ' + (fileType || '')).toLowerCase();
  return /\.(mp4|mov|webm|avi|mkv|flv|m4v)($|\?)/i.test(str) || str.includes('video');
};

export default function MaterialsTab({ project }: MaterialsTabProps) {
  const projectId = project.id || '';

  // 来源申请资料分组
  const [sourceGroups, setSourceGroups] = useState<Api.Project.SourceAssetGroupResponse[]>([]);
  const [isSourceLoading, setIsSourceLoading] = useState(false);

  // 补充资料列表
  const [supplements, setSupplements] = useState<Api.Project.ProjectAttachmentResponse[]>([]);
  const [isSupplementLoading, setIsSupplementLoading] = useState(false);

  // 上传/新增补充资料 Modal 状态
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);

  // 媒体预览 Modal 状态
  const [previewMedia, setPreviewMedia] = useState<{ title: string; url: string; isVideo: boolean } | null>(null);

  // 提示信息 Toast
  const [message, setMessage] = useState<{ type: 'success' | 'error'; msg: string } | null>(null);

  /** 加载来源申请资料 */
  const loadSourceAssets = useCallback(async () => {
    if (!projectId) return;
    setIsSourceLoading(true);
    try {
      const data = await fetchProjectSourceApplicationAssets(projectId);
      setSourceGroups(data || []);
    } catch (err) {
      console.error('获取来源申请资料失败:', err);
    } finally {
      setIsSourceLoading(false);
    }
  }, [projectId]);

  /** 加载补充资料列表 */
  const loadSupplementMaterials = useCallback(async () => {
    if (!projectId) return;
    setIsSupplementLoading(true);
    try {
      const data = await fetchProjectSupplementMaterials(projectId);
      setSupplements(data || []);
    } catch (err) {
      console.error('获取补充资料失败:', err);
    } finally {
      setIsSupplementLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    loadSourceAssets();
    loadSupplementMaterials();
  }, [loadSourceAssets, loadSupplementMaterials]);

  const handleModalSuccess = (msg: string) => {
    setMessage({ type: 'success', msg });
    loadSupplementMaterials();
  };

  const handleModalError = (msg: string) => {
    setMessage({ type: 'error', msg });
  };

  return (
    <div>
      {/* 标题 */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h3 className="font-semibold text-foreground-950 text-base flex items-center gap-2">
            <i className="ri-folder-info-line text-primary-600"></i>
            项目申请与补充资料
          </h3>
          <p className="text-xs text-foreground-500 mt-1">包含申请时归档的权属资质材料与项目运营过程中补充的证明文件</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => { loadSourceAssets(); loadSupplementMaterials(); }}
            className="px-3 py-1.5 rounded-lg border border-gray-200 text-xs text-foreground-600 hover:bg-gray-50 flex items-center gap-1 transition-all"
          >
            <i className="ri-refresh-line"></i> 刷新资料
          </button>
          <button
            onClick={() => setIsAddModalOpen(true)}
            className="px-3.5 py-1.5 rounded-lg bg-primary-500 text-foreground-950 font-medium text-xs hover:bg-primary-400 flex items-center gap-1 transition-all shadow-sm"
          >
            <i className="ri-upload-cloud-2-line font-bold"></i> 上传补充资料
          </button>
        </div>
      </div>

      {/* 区块 1：来源申请资料分组展示 */}
      <div className="mb-8">
        <div className="flex items-center gap-2 mb-3 pb-2 border-b border-background-100">
          <i className="ri-file-shield-line text-primary-600 text-sm"></i>
          <h4 className="font-bold text-foreground-900 text-xs uppercase tracking-wider">来源申请归档资料</h4>
        </div>

        {isSourceLoading ? (
          <div className="py-12 text-center text-foreground-400 text-xs">
            <i className="ri-loader-4-line animate-spin text-xl block mb-2 text-primary-500"></i>
            获取来源申请资料...
          </div>
        ) : sourceGroups.length === 0 ? (
          <div className="p-6 rounded-xl bg-background-50 border border-background-200/50 text-center text-xs text-foreground-400">
            暂无申请阶段的归档资料记录
          </div>
        ) : (
          <div className="space-y-4">
            {sourceGroups.map((groupItem, gIdx) => (
              <div key={groupItem.group || gIdx} className="p-4 rounded-xl bg-white border border-gray-200/70 shadow-2xs">
                <h5 className="font-semibold text-foreground-900 text-xs mb-3 flex items-center gap-1.5">
                  <span className="w-1.5 h-3 bg-primary-500 rounded-full"></span>
                  {groupItem.label || groupItem.group || '通用资料'}
                </h5>

                {groupItem.assets && groupItem.assets.length > 0 ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                    {groupItem.assets.map((attachment, aIdx) => {
                      const asset = attachment.asset;
                      const fileName = attachment.display_name || asset?.file_name || '';
                      const isImg = isImageFile(fileName, asset?.file_type);
                      const isVid = isVideoFile(fileName, asset?.file_type);

                      return (
                        <div key={attachment.id || aIdx} className="p-3 rounded-lg bg-gray-50/70 border border-gray-200/60 flex items-start gap-2.5">
                          <div className="w-8 h-8 rounded-lg bg-primary-100 text-primary-700 flex items-center justify-center shrink-0">
                            <i className={isVid ? 'ri-video-line text-base' : isImg ? 'ri-image-line text-base' : 'ri-file-text-line text-base'}></i>
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="font-medium text-xs text-foreground-900 truncate" title={fileName}>
                              {fileName || '归档文件'}
                            </p>
                            <div className="flex items-center gap-2 text-[11px] text-foreground-400 mt-0.5">
                              {asset?.file_size && <span>{(asset.file_size / 1024).toFixed(1)} KB</span>}
                              <span className="px-1.5 py-0.2 rounded bg-gray-200/60 text-foreground-700 text-[10px]">
                                {formatAttachmentCategory(attachment.category, attachment.is_supplement)}
                              </span>
                            </div>
                            {asset?.access_url && (
                              <div className="flex items-center gap-2 mt-1.5 font-medium text-[11px]">
                                {(isImg || isVid) && (
                                  <button
                                    type="button"
                                    onClick={() =>
                                      setPreviewMedia({
                                        title: fileName || '归档材料预览',
                                        url: asset.access_url!,
                                        isVideo: isVid,
                                      })
                                    }
                                    className="inline-flex items-center gap-0.5 text-primary-600 hover:underline cursor-pointer"
                                  >
                                    <i className={isVid ? 'ri-play-circle-line' : 'ri-eye-line'}></i> 预览
                                  </button>
                                )}
                                <a
                                  href={asset.access_url}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="inline-flex items-center gap-0.5 text-foreground-500 hover:text-foreground-700 hover:underline"
                                >
                                  <i className="ri-download-2-line"></i> 下载
                                </a>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-xs text-foreground-400 italic">该分组下暂无具体文件附件</p>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* 区块 2：项目补充资料管理列表 */}
      <div>
        <div className="flex items-center justify-between mb-3 pb-2 border-b border-background-100">
          <div className="flex items-center gap-2">
            <i className="ri-attachment-line text-primary-600 text-sm"></i>
            <h4 className="font-bold text-foreground-900 text-xs uppercase tracking-wider">项目补充资料</h4>
          </div>
          <span className="text-xs text-foreground-400">共 {supplements.length} 份文件</span>
        </div>

        {isSupplementLoading ? (
          <div className="py-12 text-center text-foreground-400 text-xs">
            <i className="ri-loader-4-line animate-spin text-xl block mb-2 text-primary-500"></i>
            获取补充资料列表...
          </div>
        ) : supplements.length === 0 ? (
          <div className="p-8 rounded-xl bg-background-50 border border-background-200/50 text-center text-xs text-foreground-400">
            <i className="ri-folder-add-line text-2xl block mb-2 text-foreground-300"></i>
            暂未上传任何补充资料，点击右上角“上传补充资料”按钮添加
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-background-200/70 text-left text-foreground-500 font-medium">
                  <th className="py-3 px-3">显示名称</th>
                  <th className="py-3 px-3">文件名称 / 资源 ID</th>
                  <th className="py-3 px-3">资料分类</th>
                  <th className="py-3 px-3">上传账号</th>
                  <th className="py-3 px-3">上传时间</th>
                  <th className="py-3 px-3 text-right">文件操作</th>
                </tr>
              </thead>
              <tbody>
                {supplements.map((item, idx) => {
                  const asset = item.asset;
                  const fileName = item.display_name || asset?.file_name || `补充文件 ${idx + 1}`;
                  const isImg = isImageFile(asset?.file_name || fileName, asset?.file_type);
                  const isVid = isVideoFile(asset?.file_name || fileName, asset?.file_type);

                  return (
                    <tr key={item.id || idx} className="border-b border-background-200/50 hover:bg-gray-50/60 transition-colors">
                      <td className="py-3 px-3 font-semibold text-foreground-900">
                        {fileName}
                      </td>
                      <td className="py-3 px-3 text-foreground-600">
                        <span className="font-mono text-[11px] bg-gray-100 px-2 py-0.5 rounded">
                          {asset?.file_name || item.upload_asset_id || '-'}
                        </span>
                      </td>
                      <td className="py-3 px-3 text-foreground-600">
                        <span className="px-2 py-0.5 rounded bg-gray-100 text-foreground-700">
                          {formatAttachmentCategory(item.category, item.is_supplement)}
                        </span>
                      </td>
                      <td className="py-3 px-3 text-foreground-500">
                        {item.uploaded_by_account_id || '-'}
                      </td>
                      <td className="py-3 px-3 text-foreground-500">
                        {formatDate(asset?.uploaded_at)}
                      </td>
                      <td className="py-3 px-3 text-right">
                        {asset?.access_url ? (
                          <div className="flex items-center justify-end gap-2">
                            {(isImg || isVid) && (
                              <button
                                type="button"
                                onClick={() =>
                                  setPreviewMedia({
                                    title: fileName,
                                    url: asset.access_url!,
                                    isVideo: isVid,
                                  })
                                }
                                className="px-2.5 py-1 rounded border border-primary-200 text-primary-600 hover:bg-primary-50 text-xs inline-flex items-center gap-1 transition-colors cursor-pointer font-medium"
                              >
                                <i className={isVid ? 'ri-play-circle-line' : 'ri-eye-line'}></i> 预览
                              </button>
                            )}
                            <a
                              href={asset.access_url}
                              target="_blank"
                              rel="noreferrer"
                              className="px-2.5 py-1 rounded border border-gray-200 text-foreground-700 hover:bg-gray-50 text-xs inline-flex items-center gap-1 transition-colors"
                            >
                              <i className="ri-download-line"></i> 下载
                            </a>
                          </div>
                        ) : (
                          <span className="text-foreground-400 italic">暂无 URL</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* 上传补充资料 Modal */}
      <AddSupplementMaterialsModal
        open={isAddModalOpen}
        projectId={projectId}
        onClose={() => setIsAddModalOpen(false)}
        onSuccess={handleModalSuccess}
        onError={handleModalError}
      />

      {/* 图片/视频媒体预览 Modal */}
      <PortfolioPreviewModal
        open={previewMedia !== null}
        title={previewMedia?.title || '资料预览'}
        isVideo={previewMedia?.isVideo ?? false}
        mediaUrl={previewMedia?.url}
        onClose={() => setPreviewMedia(null)}
      />

      {/* 提示消息 Toast */}
      <Toast
        message={message?.msg || ''}
        type={message?.type || 'info'}
        onClose={() => setMessage(null)}
      />
    </div>
  );
}
